/**
 * AdminAdmissionKitsByEmployeeTab — flips the Admission Kit view from
 * "by document type" to "by employee".  Top table shows one row per
 * employee with KPIs (signed / pending / missing badges).  Clicking a
 * row opens a side drawer with the 9 kit-doc cards + actions.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { BulkRemindPanel } from "./BulkRemindPanel";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  Search, Loader2, RefreshCw, CheckCircle2, Clock, MinusCircle, X,
  FileText, Eye, Trash2, Sparkles, AlertCircle, User, Folder,
  ArrowLeft, Users as UsersIcon, Download,
} from "lucide-react";

const KIT_ICONS = {
  ficha_registro:    "📋",
  contract:          "📝",
  cracha:            "🪪",
  epi:               "🦺",
  vt:                "🚌",
  nao_adesao:        "🩺",
  integracao_matriz: "🏢",
  integracao_filial: "🏬",
  lgpd:              "🔒",
  ordem_servico:     "✅",
};

function StatusPill({ status }) {
  if (status === "signed") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
        <CheckCircle2 className="w-3 h-3" /> Assinado
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
        <Clock className="w-3 h-3" /> Pendente
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
      <MinusCircle className="w-3 h-3" /> Não emitido
    </span>
  );
}

function Avatar({ photoB64, nome }) {
  const initials = (nome || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");
  if (photoB64) {
    const src = photoB64.startsWith("data:")
      ? photoB64
      : `data:image/jpeg;base64,${photoB64}`;
    return (
      <img
        src={src}
        alt={nome}
        className="w-9 h-12 object-cover rounded border border-slate-200 shadow-sm bg-white"
      />
    );
  }
  return (
    <div className="w-9 h-12 rounded border border-slate-200 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-500">
      {initials || <User className="w-4 h-4" />}
    </div>
  );
}

export default function AdminAdmissionKitsByEmployeeTab({ adminToken }) {
  // 'postos' = folder view (default) · 'employees' = flat list (legacy)
  const [view, setView] = useState("postos");
  // When view==='postos' and the admin clicks a folder, we drill in.
  const [activePosto, setActivePosto] = useState(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [rows, setRows] = useState([]);
  const [postoRows, setPostoRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Load the right dataset depending on the active view.
  useEffect(() => {
    let alive = true;
    const inEmployeeMode = view === "employees" || activePosto;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        if (inEmployeeMode) {
          const r = await axios.get(
            `${API}/admin/admission-kits/by-employee`,
            {
              params: {
                ...(debounced ? { search: debounced } : {}),
                ...(activePosto ? { posto: activePosto } : {}),
              },
              headers: { Authorization: `Bearer ${adminToken}` },
              timeout: 60000,
            },
          );
          if (alive) setRows(r.data?.employees || []);
        } else {
          const r = await axios.get(
            `${API}/admin/admission-kits/by-posto`,
            {
              params: debounced ? { search: debounced } : {},
              headers: { Authorization: `Bearer ${adminToken}` },
              timeout: 60000,
            },
          );
          if (alive) setPostoRows(r.data?.postos || []);
        }
      } catch (e) {
        if (!alive) return;
        const msg = e?.code === "ECONNABORTED"
          ? "A consulta demorou demais. Tente filtrar."
          : (e?.response?.data?.detail
             || "Não foi possível carregar a lista.");
        setLoadError(msg);
        toast.error(msg);
        if (inEmployeeMode) setRows([]); else setPostoRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [debounced, adminToken, refreshKey, view, activePosto]);

  const stats = useMemo(() => {
    let signed = 0, pending = 0, missing = 0, complete = 0;
    rows.forEach((r) => {
      signed += r.kit.signed; pending += r.kit.pending; missing += r.kit.missing;
      if (r.kit.signed === r.kit.expected) complete += 1;
    });
    return { signed, pending, missing, complete, total: rows.length };
  }, [rows]);

  const postoStats = useMemo(() => {
    let signed = 0, pending = 0, complete = 0, funcionarios = 0;
    postoRows.forEach((p) => {
      signed += p.signed || 0;
      pending += p.pending || 0;
      complete += p.kit_completo || 0;
      funcionarios += p.funcionarios || 0;
    });
    return { signed, pending, complete, funcionarios, total: postoRows.length };
  }, [postoRows]);

  const inPostosFolder = view === "postos" && !activePosto;
  const inEmployees = view === "employees" || !!activePosto;

  return (
    <div className="space-y-4">
      {/* View toggle / breadcrumb */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[12px]">
          {activePosto ? (
            <>
              <button
                type="button"
                onClick={() => { setActivePosto(null); setSearch(""); }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                data-testid="kit-byemp-back-postos"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Postos
              </button>
              <span className="text-slate-400">/</span>
              <span className="font-semibold text-slate-800 inline-flex items-center gap-1.5">
                <Folder className="w-3.5 h-3.5 text-amber-500" />
                {activePosto}
              </span>
            </>
          ) : (
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
              <button
                type="button"
                onClick={() => { setView("postos"); setSearch(""); }}
                className={`px-3 py-1 rounded text-[12px] font-semibold inline-flex items-center gap-1.5 transition ${
                  view === "postos"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                data-testid="kit-byemp-view-postos"
              >
                <Folder className="w-3.5 h-3.5" /> Por Posto
              </button>
              <button
                type="button"
                onClick={() => { setView("employees"); setSearch(""); }}
                className={`px-3 py-1 rounded text-[12px] font-semibold inline-flex items-center gap-1.5 transition ${
                  view === "employees"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                data-testid="kit-byemp-view-employees"
              >
                <UsersIcon className="w-3.5 h-3.5" /> Todos os funcionários
              </button>
            </div>
          )}
        </div>
      </div>

      {/* KPIs */}
      {inPostosFolder ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <KpiCard label="Postos" value={postoStats.total} accent="slate" />
          <KpiCard label="Funcionários" value={postoStats.funcionarios} accent="sky" />
          <KpiCard label="Kits completos" value={postoStats.complete}
                   sub={`/ ${postoStats.funcionarios}`} accent="emerald" />
          <KpiCard label="Assinados" value={postoStats.signed} accent="sky" />
          <KpiCard label="Pendentes" value={postoStats.pending} accent="amber" />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <KpiCard label="Funcionários" value={stats.total} accent="slate" />
          <KpiCard label="Kit completo" value={stats.complete}
                   sub={`/ ${stats.total}`} accent="emerald" />
          <KpiCard label="Assinados" value={stats.signed} accent="sky" />
          <KpiCard label="Pendentes" value={stats.pending} accent="amber" />
          <KpiCard label="Não emitidos" value={stats.missing} accent="rose" />
        </div>
      )}

      {/* Bulk cobrança WhatsApp — pendentes de Kit Admissional
          (deduplica múltiplos docs pendentes do mesmo funcionário).
          Regras anti-ban ficam no backend (bulk_reminders.py). */}
      <BulkRemindPanel
        previewUrl="/admin/admission-kits/bulk-remind/preview"
        fireUrl="/admin/admission-kits/bulk-remind"
        hasPending={(stats?.pending || 0) > 0}
        docLabel="Kit Admissional pendente"
        onDone={() => setRefreshKey((k) => k + 1)}
      />

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={inPostosFolder
              ? "Buscar posto…"
              : "Buscar por nome, matrícula ou CPF…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
            data-testid="kit-byemp-search"
          />
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
          data-testid="kit-byemp-refresh"
        >
          {loading
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {/* Folder grid (Postos) */}
      {inPostosFolder && (
        <div className="border border-slate-200 rounded-lg bg-white p-3">
          {loading && postoRows.length === 0 && (
            <div className="py-12 text-center text-slate-400 text-xs">
              <Loader2 className="w-4 h-4 inline animate-spin mr-2" />Carregando…
            </div>
          )}
          {!loading && loadError && (
            <div className="py-10 text-center text-rose-700 text-xs">
              <AlertCircle className="w-4 h-4 inline mr-2" />{loadError}
              <div className="mt-2">
                <Button size="sm" variant="outline"
                        onClick={() => setRefreshKey((k) => k + 1)}>
                  Tentar novamente
                </Button>
              </div>
            </div>
          )}
          {!loading && !loadError && postoRows.length === 0 && (
            <div className="py-10 text-center text-slate-400 text-xs">
              Nenhum posto encontrado.
            </div>
          )}
          {postoRows.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {postoRows.map((p) => {
                const completePct = p.funcionarios > 0
                  ? Math.round((p.kit_completo / p.funcionarios) * 100)
                  : 0;
                const hasPending = (p.pending || 0) > 0;
                return (
                  <button
                    type="button"
                    key={p.posto}
                    onClick={() => setActivePosto(p.posto)}
                    className="group text-left border border-slate-200 hover:border-sky-400 hover:shadow-md rounded-lg p-3 bg-gradient-to-br from-white to-slate-50 transition"
                    data-testid={`kit-byemp-posto-${p.posto}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 w-10 h-10 rounded-md bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700">
                        <Folder className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-sm truncate"
                             title={p.posto}>
                          {p.posto}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {p.funcionarios} funcionário{p.funcionarios === 1 ? "" : "s"}
                        </div>
                      </div>
                      {hasPending && (
                        <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                          <Clock className="w-3 h-3" /> {p.pending}
                        </span>
                      )}
                    </div>
                    {/* progress bar */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-[10px] font-semibold text-slate-500 mb-1">
                        <span>Kits completos</span>
                        <span className="text-slate-700">{p.kit_completo}/{p.funcionarios} · {completePct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                          style={{ width: `${completePct}%` }}
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px]">
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
                        {p.signed} assinados
                      </span>
                      {p.pending > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100">
                          {p.pending} pendentes
                        </span>
                      )}
                      {p.sample_names?.length > 0 && (
                        <span className="text-slate-400 truncate ml-auto max-w-[55%]"
                              title={p.sample_names.join(", ")}>
                          {p.sample_names.slice(0, 2).join(", ")}
                          {p.funcionarios > 2 && ` +${p.funcionarios - 2}`}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Table (employees) */}
      {inEmployees && (
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              <th className="px-3 py-2"></th>
              <th className="px-2 py-2">Nome / Matrícula</th>
              <th className="px-2 py-2 hidden md:table-cell">CPF</th>
              <th className="px-2 py-2 hidden lg:table-cell">Cargo / Posto</th>
              <th className="px-2 py-2 text-center">Assinados</th>
              <th className="px-2 py-2 text-center">Pendentes</th>
              <th className="px-2 py-2 text-center">Não emit.</th>
              <th className="px-2 py-2 hidden lg:table-cell">Última atividade</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-400 text-xs">
                <Loader2 className="w-4 h-4 inline animate-spin mr-2" />Carregando…
              </td></tr>
            )}
            {!loading && loadError && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-rose-700 text-xs">
                <AlertCircle className="w-4 h-4 inline mr-2" />{loadError}
                <div className="mt-2">
                  <Button size="sm" variant="outline"
                          onClick={() => setRefreshKey((k) => k + 1)}>
                    Tentar novamente
                  </Button>
                </div>
              </td></tr>
            )}
            {!loading && !loadError && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400 text-xs">
                Nenhum funcionário encontrado.
              </td></tr>
            )}
            {rows.map((r) => {
              const isComplete = r.kit.signed === r.kit.expected;
              return (
                <tr
                  key={r.id}
                  onClick={() => setSelectedEmpId(r.id)}
                  className="border-b border-slate-100 hover:bg-sky-50/40 cursor-pointer transition"
                  data-testid={`kit-byemp-row-${r.id}`}
                >
                  <td className="px-3 py-2">
                    <Avatar photoB64={r.photo_base64} nome={r.nome} />
                  </td>
                  <td className="px-2 py-2">
                    <div className="font-medium text-slate-800 truncate max-w-[260px]">
                      {r.nome || "—"}
                      {isComplete && (
                        <CheckCircle2 className="w-3.5 h-3.5 inline ml-1.5 text-emerald-600" />
                      )}
                    </div>
                    <div className="font-mono text-[11px] text-slate-500">
                      {r.matricula || "—"}
                    </div>
                  </td>
                  <td className="px-2 py-2 hidden md:table-cell font-mono text-[11px] text-slate-600">
                    {r.cpf || "—"}
                  </td>
                  <td className="px-2 py-2 hidden lg:table-cell text-[11px] text-slate-600">
                    <div className="truncate max-w-[180px]">{r.cargo || "—"}</div>
                    <div className="truncate max-w-[180px] text-slate-400">
                      {r.posto_servico || ""}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className="inline-block min-w-8 text-center px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                      {r.kit.signed}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className={`inline-block min-w-8 text-center px-2 py-0.5 rounded-md text-xs font-semibold border ${
                      r.kit.pending > 0
                        ? "bg-amber-50 text-amber-800 border-amber-100"
                        : "bg-slate-50 text-slate-400 border-slate-100"
                    }`}>
                      {r.kit.pending}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className={`inline-block min-w-8 text-center px-2 py-0.5 rounded-md text-xs font-semibold border ${
                      r.kit.missing > 0
                        ? "bg-rose-50 text-rose-700 border-rose-100"
                        : "bg-slate-50 text-slate-400 border-slate-100"
                    }`}>
                      {r.kit.missing}
                    </span>
                  </td>
                  <td className="px-2 py-2 hidden lg:table-cell text-[11px] text-slate-500">
                    {r.kit.last_activity_at
                      ? new Date(r.kit.last_activity_at).toLocaleString("pt-BR")
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      <EmployeeKitDrawer
        adminToken={adminToken}
        employeeId={selectedEmpId}
        onClose={() => setSelectedEmpId(null)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}

function KpiCard({ label, value, sub, accent = "slate" }) {
  const bg = {
    slate:   "from-slate-50 to-white border-slate-200",
    emerald: "from-emerald-50 to-white border-emerald-200",
    sky:     "from-sky-50 to-white border-sky-200",
    amber:   "from-amber-50 to-white border-amber-200",
    rose:    "from-rose-50 to-white border-rose-200",
  }[accent];
  return (
    <div className={`bg-gradient-to-br ${bg} border rounded-lg px-3 py-2`}>
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
        {label}
      </div>
      <div className="text-xl font-bold text-slate-900 leading-tight">
        {value}
        {sub && <span className="text-xs font-normal text-slate-400 ml-1">{sub}</span>}
      </div>
    </div>
  );
}

function EmployeeKitDrawer({ adminToken, employeeId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);  // {kind, url}

  useEffect(() => {
    if (!employeeId) { setDetail(null); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/admission-kits/employee/${employeeId}`,
          { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 15000 },
        );
        if (alive) setDetail(r.data);
      } catch (e) {
        if (alive) toast.error(e?.response?.data?.detail || "Falha");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [employeeId, adminToken]);

  if (!employeeId) return null;

  const emp = detail?.employee;
  const cards = detail?.cards || [];
  // Visible cards = drop suppressed (e.g. Integração Filial when Matriz exists).
  const visibleCards = cards.filter((c) => !c.suppressed);
  const missing = visibleCards.filter((c) => !c.doc).map((c) => c.kind);
  const signedCount = visibleCards.filter((c) => c.doc?.status === "signed").length;
  const pendingCount = visibleCards.filter((c) => c.doc?.status === "pending").length;
  // Has either integração been emitted? Used to label the Integração card.
  const hasIntegracao = cards.some(
    (c) => c.exclusive_group === "integracao" && c.doc,
  );

  const emitAll = async () => {
    if (!missing.length) return;
    setEmitting(true);
    try {
      const r = await axios.post(
        `${API}/admin/admission-kits/employee/${employeeId}/emit-all`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 60000 },
      );
      toast.success(`${r.data?.ok_count || 0} documento(s) emitido(s)!`);
      // refresh drawer + table
      const fresh = await axios.get(
        `${API}/admin/admission-kits/employee/${employeeId}`,
        { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 15000 },
      );
      setDetail(fresh.data);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao emitir");
    } finally {
      setEmitting(false);
    }
  };

  const downloadSignedZip = async () => {
    if (signedCount === 0) {
      toast.error("Não há documentos assinados para baixar.");
      return;
    }
    setDownloadingZip(true);
    try {
      const r = await axios.get(
        `${API}/admin/admission-kits/employee/${employeeId}/signed-zip`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob",
          timeout: 120000,
        },
      );
      // Try to extract the filename the server proposed; fall back to a
      // sensible default built from the employee we already have loaded.
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const slug = (emp?.matricula || "SN").toString().replace(/[^A-Za-z0-9]+/g, "-");
      const fallback = `AVI-Kit-Assinado-${slug}.zip`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (m && m[1]) || fallback;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const count = r.headers["x-included-count"] || "?";
      toast.success(`${count} documento(s) baixado(s) em ZIP.`);
    } catch (e) {
      // With blob responseType, error.response.data is a Blob — read it.
      let msg = "Falha ao baixar ZIP.";
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") {
        try {
          const t = await blob.text();
          const j = JSON.parse(t);
          if (j?.detail) msg = j.detail;
        } catch { /* ignore */ }
      }
      toast.error(msg);
    } finally {
      setDownloadingZip(false);
    }
  };

  const deleteDoc = async (doc) => {
    if (!doc?.delete_url) return;
    if (!window.confirm("Tem certeza? O documento pendente será excluído.")) return;
    try {
      await axios.delete(`${API}${doc.delete_url.replace("/api", "")}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        timeout: 15000,
      });
      toast.success("Excluído.");
      const fresh = await axios.get(
        `${API}/admin/admission-kits/employee/${employeeId}`,
        { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 15000 },
      );
      setDetail(fresh.data);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir");
    }
  };

  const previewUrlFor = (doc) => {
    // doc.preview_url is "/api/admin/.../preview"; API constant already
    // includes /api so strip it once.
    if (!doc?.preview_url) return "";
    const p = doc.preview_url.replace(/^\/api/, "");
    return `${API}${p}`;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 transition-opacity"
        onClick={onClose}
        data-testid="kit-byemp-drawer-backdrop"
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 h-full w-full md:w-[640px] bg-white shadow-2xl z-50 flex flex-col"
        data-testid="kit-byemp-drawer"
      >
        <header className="flex items-start gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
          <Avatar photoB64={emp?.photo_base64} nome={emp?.nome} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 truncate">
              {emp?.nome || (loading ? "Carregando…" : "—")}
            </div>
            <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
              <span>Matr <span className="font-mono">{emp?.matricula || "—"}</span></span>
              <span>CPF <span className="font-mono">{emp?.cpf || "—"}</span></span>
              {emp?.cargo && <span>{emp.cargo}</span>}
              {emp?.posto_servico && <span className="text-slate-400">{emp.posto_servico}</span>}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                {signedCount} assinados
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                {pendingCount} pendentes
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">
                {missing.length} não emitidos
              </span>
            </div>
          </div>
          <Button
            variant="ghost" size="icon" onClick={onClose}
            data-testid="kit-byemp-drawer-close"
          >
            <X className="w-4 h-4" />
          </Button>
        </header>

        {/* Emit Kit Completo CTA + Download signed ZIP */}
        <div className="px-4 py-3 border-b border-slate-100 bg-violet-50/50 space-y-2">
          {missing.length === 0 ? (
            <div className="flex items-center gap-2 text-[12px] text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              Kit completo — todos os 9 documentos já existem.
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="text-[12px] text-slate-700">
                <strong>{missing.length}</strong> documento(s) ainda não foram emitidos para esse funcionário.
              </div>
              <Button
                onClick={emitAll}
                disabled={emitting}
                size="sm"
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="kit-byemp-emit-all"
              >
                {emitting
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Emitindo…</>
                  : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Emitir KIT COMPLETO</>}
              </Button>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 pt-1 border-t border-violet-200/60">
            <div className="text-[11px] text-slate-600">
              <strong>{signedCount}</strong> documento(s) assinado(s) disponível(is) para download.
            </div>
            <Button
              onClick={downloadSignedZip}
              disabled={downloadingZip || signedCount === 0}
              size="sm"
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="kit-byemp-download-zip"
              title="Baixa todos os documentos assinados em um único ZIP, na ordem oficial do kit admissional"
            >
              {downloadingZip
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Preparando…</>
                : <><Download className="w-3.5 h-3.5 mr-1.5" />Baixar kit assinado (ZIP)</>}
            </Button>
          </div>
        </div>

        {/* Cards */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {!hasIntegracao && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              <strong>Integração:</strong> escolha <em>Matriz</em> OU <em>Filial</em> (apenas um deve ser emitido por funcionário). O botão "Emitir KIT COMPLETO" não emite essa peça — selecione manualmente pela aba do tipo.
            </div>
          )}
          {visibleCards.map((c) => (
            <div
              key={c.kind}
              className="border border-slate-200 rounded-md px-3 py-2.5 flex items-start gap-3 hover:border-slate-300 transition"
              data-testid={`kit-byemp-card-${c.kind}`}
            >
              <div className="text-2xl pt-0.5 select-none" aria-hidden>
                {KIT_ICONS[c.kind] || "📄"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-sm text-slate-800">{c.label}</span>
                  <StatusPill status={c.doc?.status || "missing"} />
                  {c.doc?.subkind && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 font-mono">
                      {c.doc.subkind}
                    </span>
                  )}
                </div>
                {c.doc ? (
                  <div className="text-[11px] text-slate-500 space-y-0.5">
                    <div>
                      <span className="text-slate-400">Emitido:</span>{" "}
                      {c.doc.created_at
                        ? new Date(c.doc.created_at).toLocaleString("pt-BR")
                        : "—"}
                    </div>
                    {c.doc.signed_at && (
                      <div>
                        <span className="text-slate-400">Assinado:</span>{" "}
                        {new Date(c.doc.signed_at).toLocaleString("pt-BR")}
                        {c.doc.channel_used && (
                          <span className="ml-1 text-slate-400">via {c.doc.channel_used}</span>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-400 italic">
                    Documento ainda não emitido para este funcionário.
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1">
                {c.doc && (
                  <>
                    <Button
                      size="sm" variant="outline"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => setPreviewDoc({
                        kind: c.kind, label: c.label,
                        url: previewUrlFor(c.doc),
                      })}
                      data-testid={`kit-byemp-view-${c.kind}`}
                    >
                      <Eye className="w-3 h-3 mr-1" /> Ver
                    </Button>
                    {c.doc.status === "pending" && (
                      <Button
                        size="sm" variant="outline"
                        className="h-7 px-2 text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50"
                        onClick={() => deleteDoc(c.doc)}
                        data-testid={`kit-byemp-delete-${c.kind}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Excluir
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Preview Modal */}
      {previewDoc && (
        <DocPreviewModal
          adminToken={adminToken}
          previewUrl={previewDoc.url}
          title={previewDoc.label}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </>
  );
}

function DocPreviewModal({ adminToken, previewUrl, title, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    let revoke = null;
    setBlobUrl(""); setError("");
    (async () => {
      try {
        const r = await axios.get(previewUrl, {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob",
          timeout: 30000,
        });
        const b = URL.createObjectURL(r.data);
        revoke = b;
        setBlobUrl(b);
      } catch (e) {
        setError(e?.response?.data?.detail || "Falha ao carregar preview");
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [previewUrl, adminToken]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
         onClick={onClose}
         data-testid="kit-byemp-preview-modal">
      <div className="bg-white rounded-lg shadow-2xl w-[90vw] h-[90vh] max-w-5xl flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-500" />
            <span className="font-semibold text-sm">{title}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}
                  data-testid="kit-byemp-preview-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 bg-slate-100">
          {error && (
            <div className="flex items-center justify-center h-full text-rose-700 text-sm gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {!error && !blobUrl && (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
            </div>
          )}
          {blobUrl && (
            <iframe src={blobUrl} title={title} className="w-full h-full" />
          )}
        </div>
      </div>
    </div>
  );
}
